import axios from "axios";
import { useEffect } from "react";
import secureLocalStorage from "react-secure-storage";
import { useNavigate } from "react-router";
import { PLAYMATEZAPI } from "./apiConstant";

const API_BASE_URL = String(process.env.REACT_APP_API_URL || "")
  .trim()
  .replace(/\/+$/, "");

const getStoredToken = () =>
  secureLocalStorage.getItem(process.env.REACT_APP_TOKEN_STORAGE_KEY);

const getStoredUser = () =>
  secureLocalStorage.getItem(process.env.REACT_APP_USER_STORAGE_KEY);

const getStoredUserId = () => {
  const storedUser = getStoredUser();
  return storedUser?.id || storedUser?.data?.id || "";
};

const getStoredUsersList = () =>
  secureLocalStorage.getItem(process.env.REACT_APP_USERS_LIST_KEY) || [];

const setStoredToken = (token) => {
  secureLocalStorage.setItem(process.env.REACT_APP_TOKEN_STORAGE_KEY, token);
};

const updateUsersListToken = (userId, token) => {
  const userList = getStoredUsersList();
  const updatedUserList = userList.map((item) =>
    item?.data?.id === userId ? { ...item, token } : item
  );

  secureLocalStorage.setItem(
    process.env.REACT_APP_USERS_LIST_KEY,
    updatedUserList
  );
};

const clearAuthStorage = () => {
  const currentUserId = getStoredUserId();
  const userList = getStoredUsersList().filter(
    (item) => (item?.data?.id || item?.id) !== currentUserId
  );

  secureLocalStorage.removeItem(process.env.REACT_APP_TOKEN_STORAGE_KEY);
  secureLocalStorage.removeItem(process.env.REACT_APP_USER_STORAGE_KEY);
  secureLocalStorage.setItem(process.env.REACT_APP_USERS_LIST_KEY, userList);
};

const redirectToLogin = () => {
  clearAuthStorage();

  if (navigateHandler) {
    navigateHandler("/login");
    window.location.reload();
    return;
  }

  window.location.href = "/login";
};

let refreshPromise = null;
let responseInterceptorId = null;
let navigateHandler = null;

const refreshAuthToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const currentUser = getStoredUser();
  const userId = currentUser?.id || currentUser?.data?.id;

  if (!userId) {
    throw new Error("Missing user id for token refresh");
  }

  const refreshUrl = `${API_BASE_URL}${PLAYMATEZAPI.generateNewJwtToken}/${userId}`;
  const storedToken = getStoredToken();
  const extractNewToken = (response) =>
    response?.data?.token ||
    response?.data?.data?.token ||
    response?.data?.result?.token ||
    response?.data?.jwtToken ||
    response?.data?.data?.jwtToken ||
    response?.data?.result?.jwtToken;

  const requestNewToken = async (authorizationToken) => {
    const response = await axios.get(refreshUrl, {
      headers: {
        "Content-Type": "application/json",
        key: process.env.REACT_APP_AUTH_API_KEY,
        Authorization: `Bearer ${authorizationToken}`,
      },
    });

    const newToken = extractNewToken(response);

    if (!newToken) {
      throw new Error("Token refresh response did not include a token");
    }

    return newToken;
  };

  refreshPromise = Promise.resolve()
    .then(async () => {
      if (storedToken) {
        try {
          return await requestNewToken(storedToken);
        } catch (error) {
          return await requestNewToken(process.env.REACT_APP_AUTH_API_KEY);
        }
      }

      return await requestNewToken(process.env.REACT_APP_AUTH_API_KEY);
    })
    .then((newToken) => {
      setStoredToken(newToken);
      updateUsersListToken(userId, newToken);
      return newToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const instance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
  headers: {
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
    "Content-Type": "application/json",
    "Cache-Control": "no-cache, no-store, max-age=0, must-revalidate",
    key: process.env.REACT_APP_AUTH_API_KEY,
  },
});

instance.interceptors.request.use(
  (request) => {
    const token = getStoredToken();
    request.headers = request.headers || {};

    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    } else {
      request.headers.Authorization = `Bearer ${process.env.REACT_APP_AUTH_API_KEY}`;
    }

    return request;
  },
  (requestError) => Promise.reject(requestError)
);

const attachResponseInterceptor = (navigate) => {
  navigateHandler = navigate || navigateHandler;

  if (responseInterceptorId !== null) {
    return;
  }

  responseInterceptorId = instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config;
      const status = error?.response?.status;
      const isRefreshRequest = originalRequest?.url?.includes(
        PLAYMATEZAPI.generateNewJwtToken
      );
      const hasStoredUser = Boolean(getStoredUserId());
      const shouldTryRefresh =
        status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !isRefreshRequest &&
        hasStoredUser;

      if (shouldTryRefresh) {
        originalRequest._retry = true;

        try {
          const newToken = await refreshAuthToken();
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        } catch (refreshError) {
          redirectToLogin();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};

attachResponseInterceptor();

const AxiosInterceptor = ({ children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    attachResponseInterceptor(navigate);
  }, [navigate]);

  return children;
};

export default instance;
export { AxiosInterceptor };
